import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box } from '@mui/material';
import {
  formatIndianAuto,
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  defaultAnimation,
} from './echartsConfig';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const CONFIRMED_GREEN = '#66BB6A';

const TOTAL_LABEL_RE = /^total\s*revenue\s*\(/i;

function prepareRevenuePerArea(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = data?.datasets || [];
  const ds = rawDatasets[0];
  if (!ds) {
    return { labels: [], values: [], totalArea: null, bestVenue: '—', lowestVenue: '—', avgYield: 0 };
  }

  const dsData = (ds.data || []).map(toNum);

  let totalArea = null;
  const rows = [];

  for (let i = 0; i < rawLabels.length; i++) {
    const label = rawLabels[i];
    if (TOTAL_LABEL_RE.test(label)) {
      const areaMatch = label.match(/\(([\d,.]+)\s*/);
      if (areaMatch) totalArea = areaMatch[1].replace(/,/g, '');
      continue;
    }
    if (dsData[i] > 0) {
      rows.push({ label, value: dsData[i] });
    }
  }

  rows.sort((a, b) => b.value - a.value);

  if (!rows.length) {
    return { labels: [], values: [], totalArea, bestVenue: '—', lowestVenue: '—', avgYield: 0 };
  }

  const labels = rows.map(r => r.label);
  const values = rows.map(r => r.value);
  const total = values.reduce((s, v) => s + v, 0);
  const avgYield = values.length > 0 ? total / values.length : 0;

  return {
    labels,
    values,
    totalArea,
    bestVenue: labels[0],
    bestValue: values[0],
    lowestVenue: labels[labels.length - 1],
    lowestValue: values[values.length - 1],
    avgYield,
  };
}

const VenueRevenuePerAreaChart = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name;

  const prepared = useMemo(() => prepareRevenuePerArea(data), [data]);

  const plotHeight = useMemo(
    () => Math.min(440, Math.max(200, prepared.labels.length * 32)),
    [prepared.labels.length],
  );

  const option = useMemo(() => ({
    grid: horizontalGrid,
    tooltip: {
      ...premiumTooltip,
      formatter(params) {
        const p = Array.isArray(params) ? params[0] : params;
        if (!p) return '';
        return `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:4px">${p.name}</div>`
          + `<div style="font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(p.value)} / sq.ft</div>`;
      },
    },
    xAxis: {
      ...indianValueAxis,
      position: 'bottom',
      axisLabel: {
        ...indianValueAxis.axisLabel,
        formatter: (value) => `${getCurrencySymbol()}${formatIndianAuto(value)}`,
      },
    },
    yAxis: {
      ...indianCategoryAxis,
      data: prepared.labels,
      inverse: true,
    },
    series: [
      {
        type: 'bar',
        data: prepared.values,
        barWidth: '55%',
        itemStyle: {
          color: CONFIRMED_GREEN,
          borderRadius: [0, 3, 3, 0],
        },
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.15)' } },
      },
    ],
    ...defaultAnimation,
  }), [prepared]);

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || 'Confirmed revenue per sq.ft — ranked by yield';

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      <ReportKpiStrip
        columns={2}
        items={apiKpis || [
          { label: 'Avg Revenue / Sq.Ft', value: `${getCurrencySymbol()}${formatIndianAuto(Math.round(prepared.avgYield))}` },
          { label: 'Total Venue Area', value: prepared.totalArea ? `${prepared.totalArea} sq.ft` : '—' },
        ]}
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={{ width: '100%', height: plotHeight }}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default VenueRevenuePerAreaChart;
